import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Container, 
  Grid, 
  Paper, 
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  Drawer,
  ListItemButton,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Tooltip
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Public as PublicIcon,
  Assignment as AssignmentIcon,
  Storefront as StorefrontIcon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Group as GroupIcon,
  LocalShipping as LocalShippingIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  SupportAgent as SupportAgentIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

// Import dashboard pages
import AdminDashboardHome from './Dashboard';
import AdminUsers from './Users';
import AdminBazars from './Bazars';
import AddBazar from './AddBazar';
import AdminStores from './Stores';
import AdminCategories from './Categories';
import AdminProducts from './Products';
import AdminOrders from './Orders';
import AdminSettings from './Settings';
import AdminAnalytics from './Analytics';

// Placeholder components for pages that don't exist yet
// const AdminAnalytics = () => (
//   <Box sx={{ p: 3 }}>
//     <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
//       বিশ্লেষণ ও রিপোর্ট
//     </Typography>
//     <Typography variant="body1">
//       শীঘ্রই সম্পূর্ণ হবে...
//     </Typography>
//   </Box>
// );

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 1,
}));

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [language, setLanguage] = useState('bn'); // 'bn' for Bangla, 'en' for English
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    handleLanguageMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    // In a real app, this would handle logout logic
    navigate('/login');
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    navigate(menuItems[newValue].path);
  };

  // Translations
  const translations = {
    bn: {
      title: 'অ্যাডমিন ড্যাশবোর্ড',
      dashboard: 'ড্যাশবোর্ড',
      users: 'ব্যবহারকারী',
      bazars: 'বাজার ম্যানেজার',
      stores: 'দোকান ম্যানেজার',
      categories: 'ক্যাটাগরিসমূহ',
      products: 'প্রডাক্ট লাইব্রেরী',
      orders: 'অর্ডারসমূহ',
      analytics: 'বিশ্লেষণ',
      settings: 'সেটিংস',
      profile: 'প্রোফাইল',
      logout: 'লগআউট',
      manageUsers: 'ব্যবহারকারী পরিচালনা',
      addUser: 'ব্যবহারকারী যোগ করুন',
      userRoles: 'ব্যবহারকারীর ভূমিকা',
      userPermissions: 'ব্যবহারকারীর অনুমতি',
      language: 'ভাষা',
      bangla: 'বাংলা',
      english: 'English'
    },
    en: {
      title: 'Admin Dashboard',
      dashboard: 'Dashboard',
      users: 'Users',
      bazars: 'Bazar Manager',
      stores: 'Store Manager',
      categories: 'Categories',
      products: 'Product Library',
      orders: 'Orders',
      analytics: 'Analytics',
      settings: 'Settings',
      profile: 'Profile',
      logout: 'Logout',
      manageUsers: 'Manage Users',
      addUser: 'Add User',
      userRoles: 'User Roles',
      userPermissions: 'User Permissions',
      language: 'Language',
      bangla: 'বাংলা',
      english: 'English'
    }
  };

  const t = translations[language];

  const menuItems = [
    { text: t.dashboard, icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: t.users, icon: <PeopleIcon />, path: '/admin/dashboard/users' },
    { text: t.bazars, icon: <StorefrontIcon />, path: '/admin/dashboard/bazars' },
    { text: t.stores, icon: <StoreIcon />, path: '/admin/dashboard/stores' },
    { text: t.categories, icon: <CategoryIcon />, path: '/admin/dashboard/categories' },
    { text: t.products, icon: <InventoryIcon />, path: '/admin/dashboard/products' },
    { text: t.orders, icon: <ShoppingCartIcon />, path: '/admin/dashboard/orders' },
    { text: t.analytics, icon: <BarChartIcon />, path: '/admin/dashboard/analytics' },
    { text: t.settings, icon: <SettingsIcon />, path: '/admin/dashboard/settings' },
  ];

  // Set current tab based on current path
  React.useEffect(() => {
    const currentIndex = menuItems.findIndex(item => {
      return item.path === location.pathname;
    });
    if (currentIndex !== -1) {
      setCurrentTab(currentIndex);
    }
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <AppBarStyled>
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {t.title}
          </Typography>
          
          {/* Admin Action Buttons */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            gap: 1, 
            mr: 2 
          }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<VisibilityIcon />}
              component="a"
              href="/"
              target="_blank"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              সাইট দেখুন
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AnalyticsIcon />}
              component={Link}
              to="/admin/dashboard/analytics"
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              রিপোর্ট
            </Button>
          </Box>
          
          {/* Language Switcher */}
          <IconButton 
            color="inherit" 
            onClick={handleLanguageMenuOpen}
            sx={{ mr: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          
          {/* View Site Icon */}
          <IconButton 
            color="inherit" 
            component="a" 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ mr: 1 }}
          >
            <PublicIcon />
          </IconButton>
          
          <IconButton color="inherit">
            <Badge badgeContent={5} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>এ</Avatar>
          </IconButton>
          
          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            disableScrollLock={true}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t.profile}
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t.settings}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t.logout}
            </MenuItem>
          </Menu>
          
          {/* Language Menu */}
          <Menu
            anchorEl={languageMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            disableScrollLock={true}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('bn')}
              selected={language === 'bn'}
            >
              {t.bangla}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
            >
              {t.english}
            </MenuItem>
          </Menu>
        </Toolbar>
        
        {/* Navigation Tabs - Hidden on Mobile */}
        {!isMobile && (
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'rgba(255,255,255,0.2)',
            background: 'linear-gradient(45deg, #3d1789 30%, #0a6abd 90%)'
          }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: 'white' }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.8)',
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Tab 
                  key={item.text}
                  label={item.text} 
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ minWidth: 120 }}
                />
              ))}
            </Tabs>
          </Box>
        )}
              </AppBarStyled>

        {/* Mobile Navigation Drawer */}
        <SwipeableDrawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={handleMobileDrawerClose}
          onOpen={() => setMobileDrawerOpen(true)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              backgroundColor: 'white',
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {t.title}
              </Typography>
              <IconButton onClick={handleMobileDrawerClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          <List sx={{ pt: 1 }}>
            {menuItems.map((item, index) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => handleMobileNavigation(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Quick Actions in Mobile Drawer */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
              দ্রুত কার্যক্রম
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                fullWidth
                component={Link}
                to="/admin/dashboard/users/add"
                onClick={handleMobileDrawerClose}
                sx={{ justifyContent: 'flex-start' }}
              >
                ব্যবহারকারী যোগ করুন
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<StorefrontIcon />}
                fullWidth
                component={Link}
                to="/admin/dashboard/bazars/add"
                onClick={handleMobileDrawerClose}
                sx={{ justifyContent: 'flex-start' }}
              >
                বাজার যোগ করুন
              </Button>
            </Box>
          </Box>
        </SwipeableDrawer>

        {/* Mobile Floating Action Button */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="menu"
            onClick={handleMobileDrawerToggle}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000,
              display: { xs: 'flex', md: 'none' }
            }}
          >
            <MenuIcon />
          </Fab>
        )}
        
        {/* Main Content Area with Sidebar */}
        <Box sx={{ display: 'flex', pt: { xs: 8, md: 16 } }}>
        {/* Left Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              backgroundColor: 'white',
              borderRight: '1px solid #e0e0e0',
              minHeight: 'calc(100vh - 128px)',
              p: 2,
              position: 'sticky',
              top: 128,
            }}
          >
          {/* Quick Actions */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            দ্রুত কার্যক্রম
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              component={Link}
              to="/admin/dashboard/users/add"
              sx={{ justifyContent: 'flex-start' }}
            >
              ব্যবহারকারী যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<StorefrontIcon />}
              fullWidth
              component={Link}
              to="/admin/dashboard/bazars/add"
              sx={{ justifyContent: 'flex-start' }}
            >
              বাজার যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<BarChartIcon />}
              fullWidth
              component={Link}
              to="/admin/dashboard/analytics"
              sx={{ justifyContent: 'flex-start' }}
            >
              রিপোর্ট দেখুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              fullWidth
              component={Link}
              to="/admin/dashboard/settings"
              sx={{ justifyContent: 'flex-start' }}
            >
              সিস্টেম সেটিংস
            </Button>
          </Box>
          
          {/* Quick Stats */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            আজকের পরিসংখ্যান
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                আজকের অর্ডার
              </Typography>
              <Typography variant="h4" color="primary">
                ৮৫
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                আজকের রাজস্ব
              </Typography>
              <Typography variant="h4" color="success.main">
                ৳১২,৪৫০
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                নতুন ব্যবহারকারী
              </Typography>
              <Typography variant="h4" color="info.main">
                ১৫
              </Typography>
            </Paper>
          </Box>
          
          {/* Recent System Alerts */}
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 3, 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            সিস্টেম সতর্কতা
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                পেন্ডিং অনুমোদন
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ৮টি দোকান অনুমোদনের জন্য অপেক্ষমান
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f3e5f5' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                সিস্টেম আপডেট
              </Typography>
              <Typography variant="caption" color="text.secondary">
                নতুন ভার্সন উপলব্ধ
              </Typography>
            </Paper>
          </Box>
        </Box>
        )}
        
        {/* Main Content */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            minHeight: 'calc(100vh - 128px)',
            p: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={<AdminDashboardHome />} />
            <Route path="/users" element={<AdminUsers />} />
            <Route path="/users/add" element={<div>Add User Page</div>} />
            <Route path="/users/roles" element={<div>User Roles Page</div>} />
            <Route path="/users/permissions" element={<div>User Permissions Page</div>} />
            <Route path="/bazars" element={<AdminBazars />} />
            <Route path="/bazars/add" element={<AddBazar />} />
            <Route path="/stores" element={<AdminStores />} />
            <Route path="/categories" element={<AdminCategories />} />
            <Route path="/products" element={<AdminProducts />} />
            <Route path="/orders" element={<AdminOrders />} />
            <Route path="/analytics" element={<AdminAnalytics />} />
            <Route path="/settings" element={<AdminSettings />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

// Default dashboard home component
const Dashboard = () => {
  // Mock data with Bangla content
  const recentOrders = [
    { id: 1, customer: 'মাহমুদুর রহমান', date: '২৫ মার্চ, ২০২৫', store: 'তাজা সবজি দোকান', status: 'ডেলিভারি সম্পন্ন', amount: 1250, placedBySupport: false },
    { id: 2, customer: 'ফাতেমা খাতুন', date: '২৫ মার্চ, ২০২৫', store: 'মাংস প্যারাডাইজ', status: 'প্রসেসিং', amount: 890, placedBySupport: true, supportAgent: 'সাকিব হাসান' },
    { id: 3, customer: 'আহমেদ হাসান', date: '২৪ মার্চ, ২০২৫', store: 'ফলের রাজ্য', status: 'অপেক্ষমান', amount: 2300, placedBySupport: true, supportAgent: 'নাসিরা বেগম' },
    { id: 4, customer: 'রহিমা বেগম', date: '২৪ মার্চ, ২০২৫', store: 'মিষ্টি কর্নার', status: 'শিপ করা হয়েছে', amount: 1750, placedBySupport: false },
  ];

  const topStores = [
    { id: 1, name: 'তাজা সবজি দোকান', owner: 'করিম উদ্দিন', orders: 156, revenue: 45280, rating: 4.8 },
    { id: 2, name: 'মাংস প্যারাডাইজ', owner: 'রহিম আলী', orders: 142, revenue: 78900, rating: 4.9 },
    { id: 3, name: 'ফলের রাজ্য', owner: 'সালেহা খাতুন', orders: 135, revenue: 32450, rating: 4.7 },
    { id: 4, name: 'মিষ্টি কর্নার', owner: 'আলিম মিয়া', orders: 98, revenue: 28750, rating: 4.6 },
  ];

  const systemStats = [
    { id: 1, type: 'নতুন দোকান নিবন্ধন', description: 'ফ্রেশ মার্কেট চট্টগ্রাম বাজারে যোগ দিয়েছে', time: '১ ঘন্টা আগে' },
    { id: 2, type: 'বাজার আপডেট', description: 'ঢাকা বাজারে ৫টি নতুন দোকান যুক্ত হয়েছে', time: '২ ঘন্টা আগে' },
    { id: 3, type: 'সিস্টেম মেইনটেন্স', description: 'নিয়মিত ব্যাকআপ সম্পন্ন হয়েছে', time: '৪ ঘন্টা আগে' },
    { id: 4, type: 'নতুন ফিচার', description: 'পেমেন্ট গেটওয়ে আপডেট করা হয়েছে', time: '৬ ঘন্টা আগে' },
  ];

  return (
    <Box>
      {/* First Div - Statistics Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {/* Enhanced Statistics Cards */}
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.3)', 
                  mr: { xs: 0, sm: 2 }, 
                  mb: { xs: 1, sm: 0 },
                  width: { xs: 40, sm: 48 }, 
                  height: { xs: 40, sm: 48 } 
                }}>
                  <StorefrontIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    মোট বাজার
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>২৫</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.3)', 
                  mr: { xs: 0, sm: 2 }, 
                  mb: { xs: 1, sm: 0 },
                  width: { xs: 40, sm: 48 }, 
                  height: { xs: 40, sm: 48 } 
                }}>
                  <StoreIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    মোট দোকান
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>১৪৮</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.3)', 
                  mr: { xs: 0, sm: 2 }, 
                  mb: { xs: 1, sm: 0 },
                  width: { xs: 40, sm: 48 }, 
                  height: { xs: 40, sm: 48 } 
                }}>
                  <GroupIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    মোট ব্যবহারকারী
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>১,২৪৭</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.3)', 
                  mr: { xs: 0, sm: 2 }, 
                  mb: { xs: 1, sm: 0 },
                  width: { xs: 40, sm: 48 }, 
                  height: { xs: 40, sm: 48 } 
                }}>
                  <ShoppingCartIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    মোট অর্ডার
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>৩,৫৬০</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', 
              color: '#333',
              height: '100%'
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.7)', 
                  mr: { xs: 0, sm: 2 }, 
                  mb: { xs: 1, sm: 0 },
                  width: { xs: 40, sm: 48 }, 
                  height: { xs: 40, sm: 48 } 
                }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    মাসিক রাজস্ব
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>৳৪,৫৫,৬৮০</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', 
              color: '#333',
              height: '100%'
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.7)', 
                  mr: { xs: 0, sm: 2 }, 
                  mb: { xs: 1, sm: 0 },
                  width: { xs: 40, sm: 48 }, 
                  height: { xs: 40, sm: 48 } 
                }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    মাসিক বৃদ্ধি
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>+১২.৫%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Second Div - Main Content Sections */}
      <Box>
        <Grid container spacing={2}>
          {/* Top Stores */}
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  শীর্ষ দোকানসমূহ
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<AddIcon />}
                  component={Link}
                  to="/admin/dashboard/stores"
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.5, sm: 1 },
                    px: { xs: 1, sm: 2 }
                  }}
                >
                  সব দেখুন
                </Button>
              </Box>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>দোকানের নাম</TableCell>
                        <TableCell align="right">অর্ডার</TableCell>
                        <TableCell align="right">আয়</TableCell>
                        <TableCell align="right">রেটিং</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topStores.map((store) => (
                        <TableRow key={store.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {store.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {store.owner}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{store.orders}</TableCell>
                          <TableCell align="right">৳{store.revenue.toLocaleString()}</TableCell>
                          <TableCell align="right">{store.rating}/৫</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {topStores.map((store) => (
                    <Paper key={store.id} sx={{ p: 1.5, bgcolor: '#f8f9fa' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {store.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        মালিক: {store.owner}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">
                          অর্ডার: {store.orders}
                        </Typography>
                        <Typography variant="body2">
                          আয়: ৳{store.revenue.toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                          রেটিং: {store.rating}/৫
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Orders */}
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  সাম্প্রতিক অর্ডার
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  component={Link}
                  to="/admin/dashboard/orders"
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.5, sm: 1 },
                    px: { xs: 1, sm: 2 }
                  }}
                >
                  সব দেখুন
                </Button>
              </Box>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>অর্ডার আইডি</TableCell>
                        <TableCell>গ্রাহক</TableCell>
                        <TableCell>অবস্থা</TableCell>
                        <TableCell align="right">পরিমাণ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell>#{order.id}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {order.customer}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.store}
                              </Typography>
                              {order.placedBySupport && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip 
                                    label="কাস্টমার সাপোর্ট"
                                    size="small"
                                    icon={<SupportAgentIcon />}
                                    sx={{ 
                                      bgcolor: '#e3f2fd',
                                      color: '#1976d2',
                                      fontSize: '0.6rem',
                                      height: '18px',
                                      '& .MuiChip-icon': {
                                        fontSize: '10px'
                                      }
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={order.status} 
                              color={
                                order.status === 'ডেলিভারি সম্পন্ন' ? 'success' :
                                order.status === 'শিপ করা হয়েছে' ? 'info' :
                                order.status === 'প্রসেসিং' ? 'primary' : 'warning'
                              } 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="right">৳{order.amount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {recentOrders.map((order) => (
                    <Paper key={order.id} sx={{ p: 1.5, bgcolor: '#f8f9fa' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          #{order.id}
                        </Typography>
                        <Chip 
                          label={order.status} 
                          color={
                            order.status === 'ডেলিভারি সম্পন্ন' ? 'success' :
                            order.status === 'শিপ করা হয়েছে' ? 'info' :
                            order.status === 'প্রসেসিং' ? 'primary' : 'warning'
                          } 
                          size="small" 
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {order.customer}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {order.store}
                      </Typography>
                      {order.placedBySupport && (
                        <Box sx={{ mb: 1 }}>
                          <Chip 
                            label="কাস্টমার সাপোর্ট"
                            size="small"
                            icon={<SupportAgentIcon />}
                            sx={{ 
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontSize: '0.6rem',
                              height: '18px',
                              '& .MuiChip-icon': {
                                fontSize: '10px'
                              }
                            }}
                          />
                        </Box>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        ৳{order.amount}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* System Activity */}
          <Grid item xs={12} md={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                সিস্টেম কার্যক্রম
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {systemStats.map((activity) => (
                  <Paper key={activity.id} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {activity.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {activity.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activity.time}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export { Dashboard as default, AdminDashboard }; 